// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.2
// updated  : 2026-06-20
// protected: NO  -> D-022A: Owner Assist Mode (WRITE) via header X-Effective-*,
//                   state di sessionStorage (tahan refresh, hilang saat tab ditutup).
//                   Tetap pertahankan "view as role" READ-ONLY (X-View-As-Role).
// =============================================================================
// Context auth V2: kelola session Supabase, profil (me), authHeader, login/logout,
//   + mode "lihat sebagai role" (read-only, OWNER) + Owner Assist Mode (WRITE, OWNER).
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";

const API = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext(null);

export const useV2Auth = () => useContext(Ctx);

// D-022A — peta role assist (mirror backend ASSIST_MAP) -> region & store untuk header.
// region/store WAJIB dikirim & harus cocok di backend; bila tidak -> 403.
export const ASSIST_OPTIONS = [
  { value: "KACAB_SBY", label: "Kacab SBY", region: "SBY", store: null },
  { value: "KACAB_SMD", label: "Kacab SMD", region: "SMD", store: null },
  { value: "ADMIN_PRODUKSI_SBY", label: "Admin Produksi SBY", region: "SBY", store: null },
  { value: "ADMIN_PRODUKSI_SMD", label: "Admin Produksi SMD", region: "SMD", store: null },
  { value: "ADMIN_GUDANG_SBY", label: "Admin Gudang SBY", region: "SBY", store: null },
  { value: "ADMIN_GUDANG_SMD", label: "Admin Gudang SMD", region: "SMD", store: null },
  { value: "STAFF_STORE_AREMA", label: "Staff Store Arema", region: "SBY", store: "AREMA" },
  { value: "STAFF_STORE_BORNEO", label: "Staff Store Borneo", region: "SMD", store: "BORNEO" },
];
const ASSIST_BY_ROLE = Object.fromEntries(ASSIST_OPTIONS.map((o) => [o.value, o]));

export const V2AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [me, setMe] = useState(null);
  const [booting, setBooting] = useState(true);
  const [meError, setMeError] = useState(false);  // B-8: /me GAGAL (bukan 403)
  const [config, setConfig] = useState({ app_env: "preview", uat_mode: true, uat_reset_enabled: false });
  // "Lihat sebagai role" (OWNER, read-only). Persist tahan refresh.
  const [viewAs, setViewAsState] = useState(() => localStorage.getItem("v2_view_as") || "");
  // D-022A Owner Assist Mode (OWNER, WRITE). sessionStorage: tahan refresh, hilang saat tab ditutup.
  const [assist, setAssistState] = useState(() => sessionStorage.getItem("v2_assist_role") || "");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setBooting(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    // Public config (app_env / UAT mode) — tanpa auth, untuk banner UAT di login.
    fetch(`${API}/api/v2/public-config`).then((r) => r.ok ? r.json() : null)
      .then((c) => { if (c) setConfig(c); }).catch(() => {});
    return () => sub.subscription.unsubscribe();
  }, []);

  const setViewAs = useCallback((role) => {
    if (role) {
      localStorage.setItem("v2_view_as", role);
      // mutual-exclusive: matikan assist mode bila aktifkan view-as.
      sessionStorage.removeItem("v2_assist_role");
      setAssistState("");
    } else {
      localStorage.removeItem("v2_view_as");
    }
    // SLICE S-A2 (B-5): kosongkan `me` LEBIH DULU. Gate V2App (!me -> spinner) akan meng-unmount
    // seluruh shell, lalu remount setelah loadMe selesai -> header & `me` selalu sepadan.
    // Menghapus kelas bug "header sudah berubah, state belum" (mis. banner merah Merchandize).
    setMe(null);
    setViewAsState(role || "");
  }, []);

  const setAssist = useCallback((role) => {
    if (role && ASSIST_BY_ROLE[role]) {
      sessionStorage.setItem("v2_assist_role", role);
      // mutual-exclusive: matikan view-as bila aktifkan assist.
      localStorage.removeItem("v2_view_as");
      setViewAsState("");
      setMe(null);  // B-5: lihat setViewAs
      setAssistState(role);
    } else {
      sessionStorage.removeItem("v2_assist_role");
      setMe(null);  // B-5
      setAssistState("");
    }
  }, []);

  const authHeader = useCallback(() => {
    const base = {
      Authorization: `Bearer ${session?.access_token}`,
      "Content-Type": "application/json",
    };
    // D-022A: Assist Mode (WRITE) diprioritaskan; tidak digabung dengan view-as.
    const a = assist ? ASSIST_BY_ROLE[assist] : null;
    if (a) {
      return {
        ...base,
        "X-Effective-Role": a.value,
        "X-Effective-Region": a.region,
        ...(a.store ? { "X-Effective-Store": a.store } : {}),
      };
    }
    if (viewAs) return { ...base, "X-View-As-Role": viewAs };
    return base;
  }, [session, viewAs, assist]);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { ...authHeader(), ...(opts.headers || {}) } });
    return res;
  }, [authHeader]);

  const loadMe = useCallback(async () => {
    if (!session) return;
    setMeError(false);
    // SLICE S-A2 (B-8): bedakan berhasil / 403 (belum terdaftar) / GAGAL. Sesudah B-5 `me`
    // dikosongkan dulu, jadi satu kegagalan tak boleh membuat spinner abadi -> tandai meError.
    // Satu kali percobaan ulang otomatis sebelum menyerah.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const r = await fetch(`${API}/api/v2/me`, { headers: authHeader() });
        if (r.status === 403) { setMe({ pending: true }); return; }
        if (r.ok) { setMe(await r.json()); return; }
        // non-403 & !ok -> coba sekali lagi lalu tandai gagal
      } catch (e) { /* jaringan putus -> coba lagi lalu tandai gagal */ }
    }
    setMeError(true);
  }, [session, authHeader]);

  // Reload /me saat session / viewAs / assist berubah (loadMe ikut berubah via authHeader).
  useEffect(() => { if (session) loadMe(); else { setMe(null); setMeError(false); } }, [session, loadMe]);

  const login = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
  };
  const logout = async () => {
    setViewAs(""); setAssist("");
    await supabase.auth.signOut();
    setMe(null);
  };

  return (
    <Ctx.Provider value={{ session, me, booting, meError, authHeader, api, login, logout,
                           reloadMe: loadMe, API, viewAs, setViewAs, assist, setAssist,
                           uatMode: config.uat_mode, appEnv: config.app_env,
                           resetActiveInProd: !!config.reset_active_in_production,
                           realtimeEnabled: !!config.realtime_enabled,
                           storeComplimentEnabled: !!config.store_compliment_enabled }}>
      {children}
    </Ctx.Provider>
  );
};
